"use client";

import React, { useEffect, useRef } from "react";
import { PageTrademania2Schema } from "@/sanity/pages/PageTrademania2Template";
import { FadeUp, Marquee, prefersReducedMotion } from "@/components/trademania-2/shared";

import overlay from "@/public/trademania-2/helmet-overlay.png";

const clamp = (v: number, a = 0, b = 1) => Math.min(b, Math.max(a, v));
// share of the scroll distance the copy travels on top of the normal scroll while the section comes in
const PARALLAX = 0.9;
// share of the scroll distance the copy rises on top of the normal scroll once it is past its Figma spot
const RISE = 0.4;
// share of the way the copy catches up with its parallax spot each frame, so it glides
const EASE = 0.1;
// the clip's last frame is black, so the scrub stops this many seconds before the end
const END = 0.15;

/**
 * Plain section in normal flow: nothing is pinned. The video is scrubbed by the scroll while
 * the section crosses the viewport, and the copy + marquee ride up over it with an eased parallax
 * (they travel faster than the video, reach their Figma spot and keep rising over the helmet).
 * Phones scrub the same way. Mobile browsers drop a seek that is overtaken by the next one, so a
 * new time is only set once the previous seek has landed, and iOS only paints seeks once the
 * video has played, so it is started and stopped once as the section approaches.
 */
export default function Helmet({ data }: { data: PageTrademania2Schema }) {
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const video = videoRef.current;
    const copy = copyRef.current;
    if (!section || !video || !copy) return;

    if (prefersReducedMotion()) {
      video.loop = true;
      video.play().catch(() => {});
      return;
    }

    let primed = false;
    const prime = () => {
      if (primed) return;
      primed = true;
      video
        .play()
        .then(() => video.pause())
        .catch(() => {
          // e.g. iOS Low Power Mode: try again on the next tap
          primed = false;
        });
    };
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) prime();
      },
      { rootMargin: "100% 0px" },
    );
    io.observe(section);
    window.addEventListener("touchend", prime, { passive: true });

    const phone = window.matchMedia("(max-width: 992px)");
    let current = 0;
    let lift = NaN;
    let raf = 0;
    const loop = () => {
      raf = requestAnimationFrame(loop);
      const r = section.getBoundingClientRect();
      const vh = window.innerHeight;
      if (r.bottom < -vh || r.top > vh * 2) {
        // off screen: the copy snaps to its spot on the way back instead of easing from a stale one
        lift = NaN;
        return;
      }

      // video time follows the scroll: 0 when the section enters, 1 when it has left at the top
      const target = clamp((vh - r.top) / Math.max(1, r.height + vh));
      // (skipped while the priming play is still running)
      if (video.duration && video.paused) {
        current += (target - current) * 0.12;
        const t = current * (video.duration - END);
        if (!video.seeking && Math.abs(video.currentTime - t) > 0.01) video.currentTime = t;
      }

      // parallax: the copy is on its Figma spot once the section fills the viewport (phones: is
      // centred on screen). Desktop: lower while the section enters (phones: on its spot). From
      // there it keeps rising over the helmet, faster than the page.
      const past = phone.matches ? r.top + r.height / 2 - vh / 2 : r.top;
      const shift = past > 0 ? (phone.matches ? 0 : Math.min(past, vh) * PARALLAX) : past * RISE;
      lift = Number.isNaN(lift) ? shift : lift + (shift - lift) * EASE;
      copy.style.transform = `translate3d(0,${lift.toFixed(1)}px,0)`;
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      io.disconnect();
      window.removeEventListener("touchend", prime);
    };
  }, []);

  return (
    <section
      ref={sectionRef}
      className="relative h-[82rem] overflow-clip bg-[#020202] lg:h-[calc(100svh+15rem)]">
      {/* phones (Figma 12818:174244): the 16:9 frame 877px wide, centred, 81px above the top of a
          656px block, black below (the clip's first frame is the Figma still). The clip's lower edge
          is not black like the still's, so it fades out into the block. Desktop: the frame fills the
          viewport */}
      <div className="absolute inset-x-0 top-0 h-full overflow-hidden bg-black lg:h-[100svh]">
        {/* blends the page into the sky; on phones only a short fade, as the helmet sits 35px
            below the top edge there */}
        <div className="absolute top-0 right-0 left-0 z-[5] h-[4rem] w-full bg-gradient-to-b from-[#F7F8F6] to-[#F7F8F6]/0 lg:h-[10rem] lg:to-white" />
        {data?.helmetVideo?.url && (
          <video
            ref={videoRef}
            className="absolute top-[-10.125rem] left-1/2 aspect-video w-[max(109.625rem,100%)] max-w-none -translate-x-1/2 [mask-image:linear-gradient(to_bottom,#000_60%,transparent)] object-cover [-webkit-mask-image:linear-gradient(to_bottom,#000_60%,transparent)] lg:inset-0 lg:aspect-auto lg:h-full lg:w-full lg:translate-x-0 lg:[mask-image:none] lg:object-bottom lg:[-webkit-mask-image:none]"
            src={data.helmetVideo.url}
            poster={data?.helmetPoster?.src}
            muted
            playsInline
            preload="auto"
          />
        )}
        {/* the frame's sky (#E9E8E4) is a touch darker than the page (#F7F8F6), which left a hard
            edge where the section starts. A colour-dodge lifts the light sky to the page colour at
            the top and fades out downwards; the dark helmet is left practically untouched. */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 top-0 h-[30rem] mix-blend-color-dodge lg:h-[45%]"
          style={{
            background:
              "linear-gradient(to bottom, rgb(18,19,21) 0%, rgba(18,19,21,.9) 20%, rgba(18,19,21,.65) 45%, rgba(18,19,21,.3) 72%, rgba(18,19,21,0) 100%)",
          }}
        />
        {/* Figma "Rectangle 1430107922" / "Overlay": dark fade over the lower part */}
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={overlay.src}
          alt=""
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-[52.5rem] w-full max-w-none object-fill lg:h-[62.5%]"
        />
      </div>

      {/* copy and marquee as in the Figma frame (the marquee runs past the video's lower edge) */}
      <div
        ref={copyRef}
        className="absolute inset-0 z-[1] will-change-transform">
        {data?.helmetText && (
          <FadeUp className="absolute top-[65.25rem] right-[2rem] left-[2rem] -translate-y-full lg:top-[calc(100svh-13.875rem)] lg:right-auto lg:left-[7.5rem] lg:w-[68rem]">
            <p className="text-center font-geist text-[2.5rem] leading-[1.4] font-medium text-white/80 lg:text-left lg:leading-[1.5] lg:text-white/[.48]">
              {data.helmetText}
            </p>
          </FadeUp>
        )}

        {data?.helmetMarquee && (
          <Marquee
            speed={1.1}
            className="absolute inset-x-0 top-[67.075rem] lg:top-[calc(100svh-10.375rem)]">
            <span className="shrink-0 pr-[3rem] font-owners text-[11rem] leading-none font-medium whitespace-nowrap text-[#C9D5BF] lg:pr-[12.5rem] lg:font-mona lg:text-[24.25rem] lg:leading-[1.004] lg:font-semibold lg:tracking-[-.0324em]">
              {data.helmetMarquee}
            </span>
          </Marquee>
        )}
      </div>
    </section>
  );
}
